// Filter contract for generative-canvas widgets (TASK-495). The ONE source of
// truth for "which filter props may an agent set on a widget, and what's valid".
// The UI brain (TASK-499) mirrors this server-side in `_sanitize_*`; the widget
// calls `sanitizeWidgetFilters` at its boundary so an unknown/out-of-range filter
// from either the LLM or an on-tile edit is dropped, never trusted.
//
// Enum value sets are imported from the existing filter types so this stays in
// lockstep with the real UI (`types/portfolio-views.ts`) — do not hand-copy them.
import type {
  MandateFilter,
  RiskFilter,
  PerformanceFilter,
  MultiPortfolioFilter,
} from '../types/portfolio-views';

// One allowed filter key's shape + validity rule.
export type FilterSpec =
  | { type: 'enum'; values: readonly string[]; multi?: boolean }
  | { type: 'number'; min?: number; max?: number }
  | { type: 'boolean' }
  | { type: 'string'; maxLength?: number };

// Real enum value sets (kept as const so they're reusable + match the UI).
const MANDATES: readonly MandateFilter[] = ['all', 'discretionary', 'advisory', 'execution-only'];
const RISKS: readonly RiskFilter[] = ['all', 'conservative-1-3', 'moderate-4', 'aggressive-5'];
const PERFORMANCE: readonly PerformanceFilter[] = ['all', 'outperforming', 'underperforming'];
const MULTI_PORTFOLIO: readonly MultiPortfolioFilter[] = ['all', 'single', 'multiple'];
// MyBookClient.status (hooks/useMyBookData.ts) — the book-list health dimension.
const BOOK_STATUS = ['healthy', 'attention', 'rebalance', 'no_risk_profile'] as const;
const BENCHMARKS = ['SPI', 'SMI'] as const;

// component name → { filterKey → spec }. Identity props (clientId/portfolioId) are
// NOT here: the server injects those (TASK-499) and they are not agent-tunable.
// ponytail: only the widgets that actually take filters today. Add a row when a
// widget gains a real, backend-honoured filter — an empty row is a lie to the brain.
export const FILTER_WHITELIST: Record<string, Record<string, FilterSpec>> = {
  // Client-level identity widget (TASK-495 — ClientLevelView adapted off the router/
  // global store). Only dateMode + benchmark are forwarded to the backend by
  // useClientLevelData's queryFn (→ getClientDashboardData), so only these genuinely
  // narrow results. assetClasses/currencies are intentionally ABSENT: they'd re-fetch
  // (queryKey) but getClientDashboardData ignores them — a filter that does nothing is
  // a lie to the brain. Add them once the dashboard endpoint honours them.
  ClientLevelView: {
    dateMode: { type: 'enum', values: ['latest', 'historical'] },
    benchmark: { type: 'enum', values: BENCHMARKS },
  },
  // COReviewQueue — review status narrowing (TASK-495). Single-select, mirroring the
  // component's real StatusFilter union (COReviewQueue.tsx:26); 'all' = no filter.
  COReviewQueue: {
    status: { type: 'enum', values: ['all', 'submitted', 'in_review', 'changes_requested', 'rejected'] },
  },
  // TASK-908 — `top N` read from the prompt ("her five largest positions"). Genuinely
  // honoured: `PositionsTablePanel` slices the ranked list by it, so this is not a filter
  // that does nothing. The widget fetches the whole book and ranks client-side, so there is
  // no backend narrowing to mirror.
  PositionsTable: {
    topN: { type: 'number', min: 1, max: 100 },
  },
  // TASK-906 — the threshold the RM names ("over 5%"). The endpoint's query
  // parameter and the row filter, so it genuinely narrows.
  IssuerConcentration: {
    threshold: { type: 'number', min: 0.1, max: 100 },
  },
  // TASK-904 — the security the RM named and the shock they applied to it. Both
  // reach the endpoint, so both genuinely narrow.
  SecurityExposure: {
    subject: { type: 'string', maxLength: 80 },
    shock: { type: 'number', min: -100, max: 0 },
  },
  // TASK-907 — the horizon the RM asked for ("next 12 months").
  MaturityLadder: {
    months: { type: 'number', min: 1, max: 60 },
  },
  // TASK-612 — research dossiers: the subject IS the widget's identity (which company /
  // sector to analyze), passed by the brain from the utterance.
  ResearchBrief: {
    subject: { type: 'string', maxLength: 80 },
  },
  IndustryDossier: {
    subject: { type: 'string', maxLength: 80 },
  },
  // TASK-687 (review fix) — the pre-mortem's subject is WHAT the RM is about to put to the
  // client, and they have just said it in the utterance ("how will he react to adding a
  // luxury goods holding"). Without it here the prop was stripped and the tile always
  // arrived empty, asking the RM to retype the sentence they had already typed.
  // Longer than the research subjects: this is a proposal in words, not a company name.
  ClientReaction: {
    subject: { type: 'string', maxLength: 160 },
  },
  // ClientProfileSummary (TASK-580) — display-shaping over the fetched narrative summary.
  // `language` is intentionally ABSENT: the stored row is in ONE language, so a language
  // filter could never narrow anything — a filter that does nothing is a lie to the brain.
  ClientProfileSummary: {
    sections: {
      type: 'enum',
      values: ['background', 'risk_tolerance', 'objectives', 'financial_situation', 'preferences'],
      multi: true,
    },
    minConfidence: { type: 'number', min: 0, max: 1 },
  },
  // DnaCard — display-shaping filters over data it already fetched (client-side).
  DnaCard: {
    sections: { type: 'enum', values: ['values', 'exclusions', 'tilts', 'promises', 'life_events'], multi: true },
    minConfidence: { type: 'number', min: 0, max: 1 },
    verifiedOnly: { type: 'boolean' },
  },
  // ClientBook (derived from MyBookView, TASK-495). `conflicts` is intentionally absent
  // (no client-level conflict dimension exists — it's holding-level only). Two tiers:
  //  • Tier A — status/minAum/maxAum: forwarded to /my-book/summary and filtered
  //    server-side (this task). sortBy is applied client-side (always works). `title` was
  //    here too and was removed by TASK-871 — see the note on the ClientBook row below.
  //  • Tier B — mandate/risk/performance/multiPortfolio: the backend row doesn't carry
  //    these in a filterable form yet (get_my_book_summary emits total_value/status and
  //    only a RAW risk_profile, not the bucketed RiskFilter; mandate/performance/
  //    portfolioCount aren't emitted at all — the frontend MyBookClient type over-
  //    declares them). Valid contract values, but a no-op until the SQL+assembly plumbing
  //    lands — see CLIENTBOOK_FILTERS_PENDING_BACKEND.
  // MeetingsList (TASK-666). All four narrow CLIENT-SIDE over the single RLS-scoped
  // `GET /meeting` payload — the endpoint takes no query filters today. Documented here
  // so the contract does not imply server-side narrowing it doesn't do.
  MeetingsList: {
    status: {
      type: 'enum',
      values: ['scheduled', 'live', 'in_progress', 'post_processing', 'completed', 'cancelled'],
      multi: true,
    },
    timeframe: { type: 'enum', values: ['upcoming', 'past', 'all'] },
    hasBrief: { type: 'boolean' },
    // TASK-871: `title` removed here too — MeetingsList renders it as the widget heading over
    // this same fixed filter set, so "Meetings where the client cancelled twice" could head an
    // unfiltered list. Identical to the ClientBook defect; removed with it rather than left as
    // the next one to be found.
  },
  ClientBook: {
    mandate: { type: 'enum', values: MANDATES, multi: true },
    risk: { type: 'enum', values: RISKS, multi: true },
    status: { type: 'enum', values: BOOK_STATUS, multi: true },
    performance: { type: 'enum', values: PERFORMANCE },
    multiPortfolio: { type: 'enum', values: MULTI_PORTFOLIO },
    minAum: { type: 'number', min: 0 },
    maxAum: { type: 'number', min: 0 },
    sortBy: { type: 'enum', values: ['aum_desc', 'aum_asc', 'health_asc', 'name'] },
    // TASK-871: `title` removed, mirroring `_FILTERABLE["ClientBook"]` server-side. It was
    // free text over the fixed-enum filter set above, and ClientBook renders it as the table
    // heading — so the model headed the FULL roster "Clients with >5% single-issuer
    // concentration" while no such filter exists. A title is a claim, and this one had no
    // mechanism behind it.
  },
};

// Whitelisted-but-not-yet-honoured filter keys (Tier B above). The on-tile filter bar
// (TASK-495) reads this to disable these controls so the UI never implies a filter that
// silently does nothing. Drop a key here once its backend plumbing lands.
export const CLIENTBOOK_FILTERS_PENDING_BACKEND: ReadonlySet<string> = new Set([
  'mandate',
  'risk',
  'performance',
  'multiPortfolio',
]);

const validEnum = (spec: Extract<FilterSpec, { type: 'enum' }>, v: unknown): unknown => {
  if (spec.multi) {
    const arr = (Array.isArray(v) ? v : [v]).filter((x) => spec.values.includes(x as string));
    return arr.length ? arr : undefined;
  }
  return spec.values.includes(v as string) ? v : undefined;
};

const clampNumber = (spec: Extract<FilterSpec, { type: 'number' }>, v: unknown): unknown => {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return undefined;
  return Math.min(spec.max ?? Infinity, Math.max(spec.min ?? -Infinity, n));
};

/**
 * Drop every prop key that isn't a whitelisted, valid filter for `component`.
 * Returns ONLY the sanitized filter subset (identity props like clientId are not
 * filters and are read by the widget directly). Unknown keys, wrong types, and
 * out-of-range/enum values are silently dropped/clamped — the defensive boundary
 * TASK-495 AC#3 requires, mirrored by the backend whitelist for TASK-499.
 */
export function sanitizeWidgetFilters(
  component: string,
  props: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const allowed = FILTER_WHITELIST[component];
  if (!allowed || !props) return {};
  const out: Record<string, unknown> = {};
  for (const [key, spec] of Object.entries(allowed)) {
    if (!(key in props)) continue;
    const v = props[key];
    let clean: unknown;
    if (spec.type === 'enum') clean = validEnum(spec, v);
    else if (spec.type === 'number') clean = clampNumber(spec, v);
    else if (spec.type === 'boolean') clean = typeof v === 'boolean' ? v : undefined;
    else clean = typeof v === 'string' ? v.slice(0, spec.maxLength ?? 200) : undefined;
    if (clean !== undefined) out[key] = clean;
  }
  return out;
}
